import { ref } from 'vue';
import type { HeaderProps, FooterProps, ConfigurationResponse } from '@/types/shared-types';
import { Status } from '@/constants/api.constants';
import { APP_CONFIGURATION_ROUTE, NAVIGATION_ROUTE } from '@/constants/routes.constants';
import { useQuery } from '@tanstack/vue-query';
import { handleMultilingualPrefix } from '@/utils/domainUtils';
import { checkQueryClient } from '@/utils/checkQueryclient';
import { getAppConfiguration } from '../services/app-configuration-service';

interface NavigationConfig {
    useDev: boolean;
    baseUrl?: string;
}

export type FetcherResponse = {
    header: HeaderProps;
    footer: FooterProps;
};

export const fetcher = async (config: NavigationConfig): Promise<FetcherResponse> => {
    const navRequestUrl = config.baseUrl
        ? `${config.baseUrl}${handleMultilingualPrefix(NAVIGATION_ROUTE)}`
        : handleMultilingualPrefix(NAVIGATION_ROUTE);
    return (await fetch(navRequestUrl)).json();
};

// TODO: delete useNavigation?
export default function useNavigation() {
    const headerNav = ref<HeaderProps | null>(null);
    const footerNav = ref<FooterProps | null>(null);
    const status = ref(Status.IDLE);

    async function fetchNavigation(config: NavigationConfig) {
        status.value = Status.RUNNING;

        await fetcher(config)
            .then((data) => {
                headerNav.value = data.header;
                footerNav.value = data.footer;
                status.value = Status.SUCCESS;
            })
            .catch((e) => {
                status.value = Status.ERROR;
                console.error(e);
            });
    }

    return {
        status,
        headerNav,
        footerNav,
        fetchNavigation,
    };
}

export function useNavigationQuery({ useDev, baseUrl }: NavigationConfig) {
    checkQueryClient();

    // useDev and baseUrl as key for compatibility
    const queryKey = useDev ? [`navigation:${useDev}:${baseUrl}`] : [APP_CONFIGURATION_ROUTE];

    const { data, status } = useQuery({
        queryKey,
        queryFn: () => getAppConfiguration(baseUrl),
        select: (data: ConfigurationResponse) => data?.navigation,
    });

    return {
        status,
        data,
        headerNav: data.value?.header,
        footerNav: data.value?.footer,
    };
}
